/**
 * Вкладка «Договор» панели «Чатов» — есть у любой сделки. Вид — как «Товары»
 * договора в CRM: плашка «Итого / Оплачено», под ней товары карточками — фото, цена, дни проката,
 * серия, «Товар на выбор». Данные — `GET /api/deals/{id}/contract` (те же, что вкладка «Договор»
 * карточки сделки), подробности и оплаты — по ссылкам в карточку. Сверху — повторная примерка
 * (`VisitSection`: запланировать, изменить, отменить), на товарах — её отметки и задачи товара
 * «Обещали / Ремонт / Химчистка / Подшив» с постановкой из КЦ (`ProductTasks`, владелец 22.10.26).
 * Клик по фото товара — слайдер фото модели «На модели» / «На вешалке», «фото в чат» — подборка с
 * серией в поле ответа (`SerialPhotos`, владелец 22.10.26). Договора нет — «Создать договор»
 * (`CreateContract`, право `contract_edit`): ФИО, телефон, даты и товары одной формой. Клик по цене товара —
 * окно цены (`PriceDialog`: прокат/продажа, 1–3 дня, цена из прайса или своя, владелец 23.10.26).
 */
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, TriangleAlert } from "lucide-react";
import { useState } from "react";

import { ProductTrackPills } from "@/components/chat/DealTracks";
import { ProductTaskAdd, ProductTaskList } from "@/components/chat/ProductTasks";
import { AddProduct } from "@/components/contract/AddProduct";
import { type CreateReport, CreateContract, CreateStatus } from "@/components/contract/CreateContract";
import { FieldsForm } from "@/components/contract/FieldsForm";
import { ChangesLog } from "@/components/contract/ChangesLog";
import { ContractDocuments } from "@/components/contract/ContractDocuments";
import { ContractPrints } from "@/components/contract/ContractPrints";
import { PassportBlock } from "@/components/contract/PassportBlock";
import { CityQuick, RentQuick } from "@/components/contract/QuickEdit";
import { PriceDialog } from "@/components/contract/PriceDialog";
import { ProductHistoryDialog } from "@/components/contract/ProductHistory";
import { RowActions, type RowEditCtx } from "@/components/contract/RowEdit";
import { SyncBar } from "@/components/contract/SyncBar";
import { WriteOutcome } from "@/components/contract/ui";
import { SerialCardDialog } from "@/components/chat/SerialCard";
import { type OnOffer, SerialPhotosSend, SerialPhotosViewer } from "@/components/chat/SerialPhotos";
import { type VisitOpen, VisitMark, VisitSection, visitQuery } from "@/components/chat/VisitBlock";
import { DealVideo } from "@/components/video/DealVideo";
import {
  type ContractWriteResult,
  type PickedItem,
  type RawContractRow,
  type RowPrices,
  contractChangesQuery,
  contractQuery,
  useContractWritable,
} from "@/lib/contract";
import type { VisitForm } from "@/lib/dealVisit";

import { type Contract, type ContractProduct, type DealContract, type IssueState, loadDealContract } from "@/lib/dealContract";
import { fmtPhone } from "@/components/chat/Contacts";
import { day, rentDays } from "@/lib/dealStage";
import { plural } from "@/lib/plural";
import { navObject, navUrl } from "@/nav/NavMenu";

const card = "rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3 text-[12.5px]";
const note = "p-4 text-[12.5px] text-fg-muted";
const rub = (n: number) => `${n.toLocaleString("ru-RU")} ₽`;
const many = (n: number, one: string, few: string, lot: string) => `${n} ${plural(n, one, few, lot)}`;
const pill = "rounded-full px-1.5 py-px text-[11px] font-medium leading-[16px]";

const NONE: PickedItem[] = [];

/** Повторка — общая на вкладку: строка сверху и отметки товаров открывают одну форму. */
type VisitCtx = { form: VisitForm; onOpen: (v: VisitOpen) => void } | null;
/** Сделка и чат — задачам товара; нет (удалённые товары) — без «+ задача». */
type Where = { dealId: number; chatId: number | null } | null;
/** Открыть карточку серии (`SerialCard`) по клику на номер серии товара. */
type OnSerial = (serial: number) => void;
/** Фото — в поле ответа чата; нет (вне «Чатов») — слайдер без отправки. */
type Offer = OnOffer | undefined;
/** Окно «История» товара (выдача, цена, правки). */
type OnHistory = (p: ContractProduct) => void;

/** Вкладки над товарами: выдача по `sMove_deal`; услуги — только во «Всех» (их не выдают). */
type IssueTab = "all" | IssueState;
const ISSUE_TABS: [IssueTab, string][] = [
  ["all", "Все"],
  ["issued", "Выдано"],
  ["none", "Не выдано"],
  ["returned", "Возвращено"],
];
const issueOf = (p: ContractProduct): IssueState => p.issue ?? (p.state === "возвращён" ? "returned" : p.state ? "issued" : "none");
const inTab = (p: ContractProduct, t: IssueTab) => t === "all" || (!p.service && issueOf(p) === t);
const STATE_CLS: Record<IssueState, string> = { issued: "bg-warn-bg text-warn", returned: "bg-ok-bg text-ok", none: "border border-border text-fg" };
const short = (iso: string | null | undefined) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)} ${iso.slice(11, 16)}` : "");

function Product({ p, days, visit, where, edit, prices, onSerial, onOffer, onHistory }: { p: ContractProduct; days: number | null; visit: VisitCtx; where: Where; edit: RowEditCtx | null; prices: RowPrices | undefined; onSerial: OnSerial; onOffer: Offer; onHistory: OnHistory }) {
  const gone = Boolean(p.deleted);
  const [photos, setPhotos] = useState(false);
  const [priceOpen, setPriceOpen] = useState(false);
  const title = `${p.name}${p.serial ? ` · ${p.serial}` : ""}`;
  return (
    <li className={`flex gap-2.5 rounded-[var(--radius-card)] border border-border bg-bg p-2 ${gone ? "opacity-60" : ""}`} data-annot={`товар ${p.serial ?? p.name}`}>
      {p.photo && p.serial ? (
        <button type="button" onClick={() => setPhotos(true)} className="flex-none" title="Все фото модели: на модели и на вешалке">
          <img src={p.photo} alt="" loading="lazy" className="h-[84px] w-[60px] rounded-[var(--radius-ui)] object-cover" />
        </button>
      ) : p.photo ? (
        <a href={p.photo} target="_blank" rel="noreferrer" className="flex-none" title="Открыть фото">
          <img src={p.photo} alt="" loading="lazy" className="h-[84px] w-[60px] rounded-[var(--radius-ui)] object-cover" />
        </a>
      ) : (
        <div className="grid h-[84px] w-[60px] flex-none place-items-center rounded-[var(--radius-ui)] bg-bg-subtle text-[11px] text-fg-faint">
          {p.service ? "услуга" : "нет фото"}
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          {edit && !gone ? (
            <button
              type="button"
              onClick={() => setPriceOpen(true)}
              className="text-[15px] font-semibold tabular-nums underline decoration-dotted decoration-fg-faint underline-offset-4 hover:text-accent-text hover:decoration-solid"
              title="Изменить цену: прокат или продажа, 1–3 дня, цена из прайса или своя"
            >
              {rub(p.price)}
            </button>
          ) : (
            <span className={`text-[15px] font-semibold tabular-nums ${gone ? "line-through" : ""}`}>{rub(p.price)}</span>
          )}
          {!p.is_sale && !p.service && days && <span className="text-fg-muted">{many(days, "день", "дня", "дней")}</span>}
          {p.price_type && (
            <span className="text-[11.5px]" style={p.price_color && !gone ? { color: p.price_color } : undefined}>
              {p.price_type}
            </span>
          )}
        </div>
        <div className="break-words">
          {p.name}{" "}
          {p.serial && (
            <button type="button" onClick={() => onSerial(p.serial!)} className="font-mono text-fg-muted hover:text-accent-text hover:underline" title="Карточка серии: место, брони, повторки, задачи, цена">
              · {p.serial}
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {p.is_select && <span className={`${pill} bg-err text-white`}>Товар на выбор</span>}
          {p.is_sale && <span className={`${pill} bg-err text-white`}>Продажа</span>}
          {p.state && (
            <button
              type="button"
              onClick={() => onHistory(p)}
              className={`${pill} ${STATE_CLS[issueOf(p)]}`}
              title={p.moved ? `${p.state} ${short(p.moved.at)}${p.moved.who ? ` · ${p.moved.who}` : ""} — история` : "История товара"}
            >
              {p.state}
              {p.moved?.at ? ` ${short(p.moved.at)}` : ""}
              {p.moved?.who ? ` · ${p.moved.who}` : ""}
            </button>
          )}
          {p.tailor && <span className={`${pill} bg-ok-bg text-ok`}>подшив · {p.tailor.status ?? "—"}</span>}
          {gone && <span className="text-[11.5px] text-err">удалён</span>}
          {visit && !gone && !p.service && <VisitMark form={visit.form} serial={p.serial} onOpen={visit.onOpen} />}
          {where && !gone && p.serial && <ProductTrackPills dealId={where.dealId} serial={p.serial} />}
        </div>
        {p.comment && <div className="text-[12px] text-warn">«{p.comment}»</div>}
        <ProductTaskList tasks={p.tasks ?? []} />
        {!p.service && p.serial && (
          <div className="flex flex-wrap items-center gap-x-3">
            {where && !gone && <ProductTaskAdd p={p} dealId={where.dealId} chatId={where.chatId} />}
            {where && !gone && onOffer && <SerialPhotosSend serial={p.serial} name={p.name} onOffer={onOffer} />}
            <button type="button" onClick={() => onHistory(p)} className="text-[12px] text-accent-text hover:underline" title="Кто и когда выдал и вернул, цена, правки">
              история
            </button>
          </div>
        )}
        {edit && <RowActions ctx={edit} />}
      </div>
      {priceOpen && edit && <PriceDialog ctx={edit} prices={prices} title={title} onClose={() => setPriceOpen(false)} />}
      {photos && p.serial && (
        <SerialPhotosViewer serial={p.serial} name={p.name} fallback={p.photo} onClose={() => setPhotos(false)} onOffer={gone ? undefined : onOffer} />
      )}
    </li>
  );
}

function ContractCard({ c, dealId, chatId, base, visit, onSerial, onOffer, pending, onPendingTaken }: { c: Contract; dealId: number; chatId: number | null; base: string; visit: VisitCtx; onSerial: OnSerial; onOffer: Offer; pending: PickedItem[]; onPendingTaken: () => void }) {
  const [showDeleted, setShowDeleted] = useState(false);
  const [fields, setFields] = useState(false);
  const [tab, setTab] = useState<IssueTab>("all");
  const [history, setHistory] = useState<ContractProduct | null>(null);
  const live = c.products.filter((p) => !p.deleted);
  const shown = live.filter((p) => inTab(p, tab));
  const goods = live.filter((p) => !p.service);
  const deleted = c.products.filter((p) => p.deleted);
  const days = rentDays(c.rent.start, c.rent.end);
  // Договор и журнал дочитываем отдельно: карточка ниже показывается сразу, без ожидания.
  const raw = useQuery(contractQuery(c.id));
  const log = useQuery(contractChangesQuery(c.id));
  // Итог последней записи строки (строка после удаления уходит из списка — итог держим здесь).
  const [outcome, setOutcome] = useState<{ ok: string; r: ContractWriteResult } | null>(null);
  // Правка — по праву, при включённой записи и только когда договор (с версией) прочитан и он — этой
  // сделки (ответ сервера мог прийти не о том договоре); `ContractProduct.id` = id строки.
  const writable = useContractWritable();
  const foreign = raw.data != null && String(raw.data.dogovor.dealID ?? "") !== String(dealId);
  const can = writable && raw.data != null && !foreign;
  const rows = new Map<number, RawContractRow>(raw.data ? [...raw.data.products, ...raw.data.deleted].map((r) => [r.id, r]) : []);
  const onDone = (ok: string, r: ContractWriteResult) => setOutcome({ ok, r });
  const edit = (p: ContractProduct): RowEditCtx | null => {
    const row = rows.get(p.id);
    return can && raw.data && row ? { row, dogovorId: c.id, version: raw.data.version, dealId, chatId, service: p.service, onDone } : null;
  };
  const crm = navObject("dogovorID")?.items.find((i) => i.sys === "crm");
  const title = (
    <>
      Договор <span className="font-mono">{c.id}</span>
    </>
  );
  return (
    <section className={`${card} flex flex-col gap-2`} data-annot={`договор ${c.id}`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
        {crm ? (
          <a
            href={navUrl(crm, "dogovorID", c.id)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-[14px] font-medium text-accent-text hover:underline"
            title={`Открыть договор ${c.id} в CRM`}
          >
            {title} <ExternalLink size={12} aria-hidden />
          </a>
        ) : (
          <span className="text-[14px] font-medium">{title}</span>
        )}
        {/* Цвет статуса CRM подобран под светлый фон — кладём его фоном метки, не цветом букв. */}
        <CityQuick c={c} raw={raw.data ?? null} dealId={dealId} chatId={chatId} can={can} />
        {c.status && (
          <span className={`${pill} ${c.status_color ? "text-white" : "border border-border"}`} style={c.status_color ? { background: c.status_color } : undefined}>
            {c.status}
          </span>
        )}
        <ContractDocuments dogovorId={c.id} chatId={chatId} passport={raw.data?.passport} onOffer={onOffer} />
        <a
          href={`${base}/deals/${dealId}?tab=contract`}
          target="_blank"
          rel="noreferrer"
          className="ml-auto text-[12px] text-accent-text hover:underline [div.ml-auto+&]:ml-0"
          title="Договор целиком — документы, правки, подшив — в карточке сделки"
        >
          подробно →
        </a>
      </div>
      <div className="text-fg-muted">
        от {day(c.created.at)}
        {c.created.who ? ` · ${c.created.who}` : ""}
      </div>
      {c.customer.phone && (
        <div data-annot={`телефон договора ${c.id}`}>
          <span className="text-fg-muted">Телефон</span> <span className="font-medium tabular-nums">{fmtPhone(c.customer.phone)}</span>
        </div>
      )}
      <RentQuick c={c} raw={raw.data ?? null} dealId={dealId} chatId={chatId} can={can} />
      {/* Как «Сводка договора» CRM: «оплачено дней» заполнено = «Неточная дата». */}
      {c.days_paid ? (
        <div className="flex flex-wrap items-center gap-x-1.5 text-[12px] font-medium text-warn" data-annot={`неточная дата договора ${c.id}`}>
          <TriangleAlert size={12} aria-hidden />
          Неточная дата · оплачено {many(c.days_paid, "день", "дня", "дней")}
        </div>
      ) : null}
      {c.warnings.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-1.5 text-[12px] font-medium text-err">
          <TriangleAlert size={12} aria-hidden />
          {c.warnings.join(" · ")}
        </div>
      )}
      {writable && foreign && <div className="text-[12px] text-warn">Договор относится к другой сделке — правка недоступна.</div>}
      {can && raw.data && !fields && (
        <button type="button" onClick={() => setFields(true)} className="self-start text-[12px] text-accent-text hover:underline">
          изменить данные
        </button>
      )}
      {can && raw.data && fields && (
        <FieldsForm dealId={dealId} dogovorId={c.id} chatId={chatId} contract={raw.data} onClose={() => setFields(false)} />
      )}
      <SyncBar dealId={dealId} dogovorId={c.id} chatId={chatId} stale={c.sync_stale} changes={log.data} can={can} />
      {raw.data && <PassportBlock dogovorId={c.id} passport={raw.data.passport} />}
      {raw.isError && <div className="text-[12px] text-err">Паспорт не загрузился: {raw.error.message}</div>}
      {outcome && <WriteOutcome r={outcome.r} ok={outcome.ok} onClose={() => setOutcome(null)} />}
      {goods.length > 0 && (
        <div className="flex flex-wrap gap-1" role="tablist" data-annot={`выдача товаров договора ${c.id}`}>
          {ISSUE_TABS.map(([k, label]) => {
            const n = live.filter((p) => inTab(p, k)).length;
            return (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={tab === k}
                onClick={() => setTab(k)}
                className={`rounded-full px-2.5 py-0.5 text-[12px] ${tab === k ? "bg-accent text-white" : "border border-border bg-bg text-fg hover:bg-bg-subtle"}`}
              >
                {label} <span className="tabular-nums opacity-75">{n}</span>
              </button>
            );
          })}
        </div>
      )}
      <ul className="flex flex-col gap-1.5" data-annot={`товары договора ${c.id}`}>
        {shown.map((p) => (
          <Product key={p.id} p={p} days={days} visit={visit} where={{ dealId, chatId }} edit={edit(p)} prices={raw.data?.prices?.[String(p.id)]} onSerial={onSerial} onOffer={onOffer} onHistory={setHistory} />
        ))}
        {showDeleted && deleted.map((p) => <Product key={p.id} p={p} days={days} visit={null} where={null} edit={edit(p)} prices={undefined} onSerial={onSerial} onOffer={undefined} onHistory={setHistory} />)}
      </ul>
      {live.length === 0 && <div className="text-fg-muted">Товаров в договоре нет.</div>}
      {live.length > 0 && shown.length === 0 && <div className="text-fg-muted">Здесь товаров нет.</div>}
      {history && <ProductHistoryDialog dealId={dealId} row={history.id} title={`${history.name}${history.serial ? ` · ${history.serial}` : ""}`} onClose={() => setHistory(null)} />}
      {can && raw.data && (
        <AddProduct
          dealId={dealId}
          dogovorId={c.id}
          chatId={chatId}
          version={raw.data.version}
          // Пересечения с бронями — по датам договора, их нет — по датам сделки.
          rentStart={c.rent.start && c.rent.end ? c.rent.start : c.deal_rent.start}
          rentEnd={c.rent.start && c.rent.end ? c.rent.end : c.deal_rent.end}
          exclude={raw.data.products.flatMap((r) => (r.serial ? [r.serial] : []))}
          initial={pending}
          onTaken={onPendingTaken}
        />
      )}
      {deleted.length > 0 && (
        <button type="button" onClick={() => setShowDeleted((v) => !v)} className="self-start text-[12px] text-accent-text hover:underline">
          {showDeleted ? "скрыть удалённые" : `показать удалённые (${deleted.length})`}
        </button>
      )}
      {log.data && <ChangesLog dealId={dealId} dogovorId={c.id} chatId={chatId} changes={log.data} can={can} />}
      {log.isError && <div className="text-[12px] text-err">Журнал правок не загрузился: {log.error.message}</div>}
      <ContractPrints dogovorId={c.id} dealId={dealId} />
    </section>
  );
}

type Prefill = { phone: string | null; name: string };

function Body({ d, dealId, chatId, base, onOffer, prefill }: { d: DealContract; dealId: number; chatId: number | null; base: string; onOffer: Offer; prefill: Prefill }) {
  const can = useContractWritable();
  // Ход и итог «Создать договор» — здесь: договор появится, форма сменится карточкой, а отчёт останется.
  const [made, setMade] = useState<CreateReport | null>(null);
  const report = made?.dealId === dealId ? made : null;
  // Повторка — своим запросом: не загрузилась (CRM, старый сервер) — договор показываем без неё.
  const v = useQuery(visitQuery(dealId));
  const [open, setOpen] = useState<VisitOpen>(null);
  const [serial, setSerial] = useState<number | null>(null);
  const serialCard = serial != null && (
    <SerialCardDialog
      serial={serial}
      base={base}
      onClose={() => setSerial(null)}
      onOffer={onOffer && ((text, files) => (onOffer(text, files), setSerial(null)))}
    />
  );
  const visit: VisitCtx = v.data ? { form: v.data, onOpen: setOpen } : null;
  const top = v.data && (
    <div className={card} data-annot="повторка договора">
      <VisitSection form={v.data} dealId={dealId} chatId={chatId} open={open} setOpen={setOpen} />
    </div>
  );
  if (d.contracts.length === 0)
    return (
      <div className="flex flex-col gap-2 p-3">
        {top}
        {can ? (
          <CreateContract dealId={dealId} chatId={chatId} phone={prefill.phone} name={prefill.name} report={report} onReport={setMade} />
        ) : (
          <div className="text-[12.5px] text-fg-muted">Договора нет.</div>
        )}
        <DealVideo dealId={dealId} chatId={chatId} base={base} />
      </div>
    );
  const m = d.money;
  const positions = d.contracts.reduce((n, c) => n + c.money.positions, 0);
  return (
    <div className="flex flex-col gap-2 p-3">
      {top}
      {report && <CreateStatus r={report} onClose={() => setMade(null)} />}
      {/* Плашка — как в CRM: итог с числом товаров, оплачено красным, пока оплачено не всё. */}
      <div className="rounded-[var(--radius-card)] border border-accent/30 bg-accent/10 p-3 text-[13px]" data-annot="итог договора">
        <div>
          <span className="text-fg-muted">Итого:</span> <span className="font-semibold tabular-nums">{rub(m.total)}</span>
          <span className="text-fg-muted"> · {many(positions, "товар", "товара", "товаров")}</span>
        </div>
        <div>
          <span className="text-fg-muted">Оплачено:</span>{" "}
          <span className={`font-semibold tabular-nums ${m.due > 0 ? "text-err" : "text-ok"}`}>{rub(m.paid)}</span>
          {m.due > 0 && <span className="text-fg-muted"> · к оплате {rub(m.due)}</span>}
          <a href={`${base}/deals/${dealId}?tab=payments`} target="_blank" rel="noreferrer" className="ml-2 text-[12px] text-accent-text hover:underline">
            оплаты →
          </a>
        </div>
      </div>
      {d.contracts.map((c) => (
        <ContractCard
          key={c.id}
          c={c}
          dealId={dealId}
          chatId={chatId}
          base={base}
          visit={visit}
          onSerial={setSerial}
          onOffer={onOffer}
          // Не добавилось при создании — ждёт в «Добавить в договор» этого договора, выбирать заново не нужно.
          // Номер не узнали (договор уже был, исход неизвестен) — отдаём единственному договору сделки.
          pending={report && (report.dogovorId === c.id || (report.dogovorId === null && d.contracts.length === 1)) ? report.rest : NONE}
          onPendingTaken={() => setMade((m) => (m ? { ...m, rest: [] } : m))}
        />
      ))}
      <DealVideo dealId={dealId} chatId={chatId} base={base} />
      {serialCard}
    </div>
  );
}

export function ContractTab({
  dealId,
  chatId = null,
  base = "",
  onOffer,
  phone = null,
  name = "",
}: {
  dealId: number;
  chatId?: number | null;
  base?: string;
  /** Положить фото товара с подписью в поле ответа чата; нет — слайдер без отправки. */
  onOffer?: OnOffer | undefined;
  /** Активный номер чата и имя чата — подсказка «Создать договор» (ФИО и телефон). */
  phone?: string | null;
  name?: string;
}) {
  const q = useQuery({ queryKey: ["deal-contract", dealId], queryFn: () => loadDealContract(dealId), staleTime: 60_000 });
  // Placeholder (Refine: `keepPreviousData`) — договоры ПРОШЛОЙ сделки: не показывать и не давать править.
  if (q.isPending || q.isPlaceholderData) return <div className={note}>Загружаю договор из CRM…</div>;
  if (q.isError) return <div className={`${note} text-err`}>Не удалось получить договор: {q.error.message}</div>;
  // Ключ — сделка: формы, выбор товаров и отчёт создания не переезжают на другую сделку.
  return <Body key={dealId} d={q.data} dealId={dealId} chatId={chatId} base={base} onOffer={onOffer} prefill={{ phone, name }} />;
}
